import { IOrder } from './order.model';

export interface IOrderDocument {
  _id?: string;
  employeeId: string;
  tempCustomer?: string; // Temporärer Kunde (free text)
  palletNumber?: string; // Palettennummer
  palletBoxType?: PalletBoxType; // Paletten-/Box-Typ
  weDate: Date; // WE Datum (Wareneingang)
  deliveryDates?: ILiefertermin[]; // Liefertermine
  order?: IOrder | null; // Kunde (selected from existing orders)
  orderQuantity?: string; // Auftragsmenge (free text, e.g. "Siehe LS")
  partialQuantity?: string; // Teilmenge (free text)
  componentType?: string; // Bauteilart
  gurtbreite?: GurtbreiteType; // Gurtbreite (tape/belt width)
  factory: WerkType; // Werk
  processing: ProcessingType[]; // Bearbeitung
  activities: ActivityType[]; // Tätigkeiten
  remark?: string; // Bemerkung
  observeSpecification: boolean; // Spezifikation beachten
  status: OrderDocumentStatusEnum; // Status (manually set workflow stage)
  positions: IOrderDocumentPosition[]; // Positionen (work log entries)
  falsePositions?: IOrderDocumentFalsePosition[]; // Fehlererfassung (recorded defects)
  createdAt?: Date;
  updatedAt?: Date;
}

// One Auftragsbegleitschein of an order, as listed by GET /api/order-document/order/:orderId
// (the page's switcher between the order's Auftragsbegleitscheine).
export type IOrderDocumentSummary = Pick<IOrderDocument, '_id' | 'status' | 'factory' | 'palletNumber' | 'palletBoxType' | 'createdAt'>;

// A single delivery date option for the order document, optionally marked as binding
export interface ILiefertermin {
  date: Date; // Datum
  isBinding: boolean; // Lieferdatum sicher
  label?: string; // Bezeichnung
}

// A single flattened Liefertermin row for the production TV overview board (see
// GET /api/liefertermine-overview). One order document may contribute several rows.
export interface ILieferterminOverviewRow {
  orderDocumentId: string; // Source order document _id
  date: Date; // Liefertermin (Datum)
  isBinding: boolean; // "sicher"
  auftragsNr: string; // Auftrags-Nr. (order.internalOrderId)
  kunde: string; // Kunde (order.companyName, fallback tempCustomer)
  palletNumber: string; // Pal./Box-Nr.
  palletBoxType: PalletBoxType | ''; // Paletten-/Box-Typ
  gurtbreite: GurtbreiteType | ''; // Gurtbreite ('' if unset)
  label: string; // Infotext (Liefertermin-Bezeichnung)
  werk: WerkType | ''; // Werk ('' if unset)
}

// The kind of change a socket notification payload describes. Mirrors the backend's
// ChangeAction enum (backend/src/services/socketService.ts).
export enum ChangeAction {
  CREATED = 'created',
  UPDATED = 'updated',
  DELETED = 'deleted',
  STATUS_CHANGED = 'statusChanged',
}

// WebSocket notification pushed to the TV overview board when an order document's Liefertermine
// or status changes (see the backend 'liefertermine:changed' event / 'liefertermine' room).
export interface LieferterminNotification {
  action: ChangeAction;
  orderDocumentId?: string;
  timestamp: Date;
}

// A single work-log entry recorded against the order document
export interface IOrderDocumentPosition {
  _id?: string;
  posNr: string; // Pos.
  date: Date; // Datum
  employeeNumber: string; // Pers.-Nr.
  activity: PositionActivityType; // Tätigkeit
  stueck?: number | null; // Stück
  time: string; // Zeit, formatted as "H:MMh" (e.g. "2:45h")
  bemerkung?: string; // Bemerkung
}

// A single defect recorded in the Fehlererfassung. Deliberately independent of the positions: the
// "Fehler erfassen" button only copies a position's values into a new entry, no link is kept.
// Mirrors the backend's IOrderDocumentFalsePosition (backend/.../orderDocumentModel.ts).
export interface IOrderDocumentFalsePosition {
  _id?: string;
  posNr: string; // Pos.
  date: Date; // Datum
  employeeNumber: string; // Pers.-Nr. of whoever did the faulty work (responsible for the failure)
  inspectorNumber?: string; // Pers.-Nr. of whoever found the failure (only migrated entries lack it)
  code: FehlerCode; // Fehler
  quantity: number; // Stück (faulty pieces)
  remark?: string | null; // Bemerkung
}

// A PDF uploaded on the Dokumente tab (stored in GridFS, not embedded in the order document).
// Mirrors the backend's IOrderDocumentFile (backend/.../orderDocumentFileModel.ts).
export interface IOrderDocumentFile {
  _id: string;
  orderDocumentId: string; // owning Auftragsbegleitschein (the tab lists those of all siblings)
  originalName: string; // Dateiname
  size: number; // bytes
  uploadDate: string;
  uploadedBy: string | null; // userId
  uploadedByName: string | null;
  exportDate: string | null; // set by the nightly DMS export; afterwards only ADMIN may delete it
}

export enum WerkType {
  WERK_1 = 'WERK_1',
  WERK_2 = 'WERK_2',
  WERK_3 = 'WERK_3',
}

// Gurtbreite (tape/belt width) options. String values are self-describing; enum keys can't start
// with a digit. Mirrors the backend's GurtbreiteType (backend/.../orderDocumentModel.ts).
export enum GurtbreiteType {
  MM_8 = '8mm',
  MM_12 = '12mm',
  MM_16 = '16mm',
  MM_24 = '24mm',
  MM_32 = '32mm',
  MM_44 = '44mm',
  MM_56 = '56mm',
  MM_72 = '72mm',
  MM_88 = '88mm',
  MM_104 = '104mm',
  MIXED = 'mixed',
}

export enum PalletBoxType {
  PALETTE = 'PALETTE',
  BOX = 'BOX',
}

export enum ProcessingType {
  RADIAL = 'RADIAL',
  SMD = 'SMD',
}

export enum ActivityType {
  SCHNEIDEN = 'SCHNEIDEN',
  BIEGEN = 'BIEGEN',
  TROCKNEN = 'TROCKNEN',
  VEREINZELN = 'VEREINZELN',
  SPLEISSEN = 'SPLEISSEN',
  DREHEN = 'DREHEN',
  GURTEN = 'GURTEN',
  ENTGURTEN_NEUGURTEN = 'ENTGURTEN_NEUGURTEN',
  KONFEKTIONIEREN = 'KONFEKTIONIEREN',
}

export enum PositionActivityType {
  KONFEKTIONIEREN = 'KONFEKTIONIEREN',
  GURTEN = 'GURTEN',
  PRUEFEN = 'PRUEFEN',
  NACHARBEIT = 'NACHARBEIT',
}

export enum OrderDocumentStatusEnum {
  WARENEINGANG = 'WARENEINGANG',
  BUERO = 'BUERO',
  FERTIGUNG = 'FERTIGUNG',
  ENDKONTROLLE = 'ENDKONTROLLE',
  ABGESCHLOSSEN = 'ABGESCHLOSSEN',
}

// Error codes recordable against a false position (Fehlererfassung). Keys/values match the printed
// codes exactly (case preserved, e.g. `Fr` vs `F`) since these are shop-floor paperwork codes.
export enum FehlerCode {
  // Radial
  AR = 'AR', // Entgurten / Neugurten
  C = 'C', // Beschädigte Anschlüsse
  BR = 'BR', // Beschädigte Gehäuse
  Fr = 'Fr', // Lücken ausschneiden
  G = 'G', // Ersatzgurt einsetzen
  H = 'H', // Splicestelle erneuern
  VR = 'VR', // Vorlauf / Nachlauf (shared with SMD)
  K = 'K', // Drähte außerhalb Klebeband
  LR = 'LR', // Polarität richtigstellen (Radial)
  NRAD = 'NRAD', // Nacharbeitsscheine Radial
  // SMD
  DN = 'DN', // Deckfolie neu
  LS = 'LS', // Polarität richtigstellen (SMD)
  F = 'F', // Fremdkörper entfernen
  BS = 'BS', // Defekte Teile ersetzen
  FS = 'FS', // Lücken (SMD)
  AS = 'AS', // Entgurten / Neugurten (SMD)
  DP = 'DP', // Doppelte Teile
  MV = 'MV', // Mittelversiegelung
  BIS = 'BIS', // extern BIS
  NSMD = 'NSMD', // Nacharbeitsscheine
  // Allgemeines
  SF = 'SF', // Stückzahlfehler
  FE = 'FE', // Falsche Etiketten
  XYZ = 'XYZ', // Sonstiges
}
